import {
  boostLadder,
  breakevenPctFromLadder,
  getSlipBreakevens,
  promoBlocksBoost,
  slipTypeLegCount,
  type SlipLadder,
  type SlipTypeBreakeven,
} from "@/components/optimizer/props-slip-breakevens";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import { playText, type PlayLine } from "@/components/fantasy-props/model";
import { PROMO_KINDS, type PromoKind } from "@/lib/odds-screen/live/types";
import { isStoredBetLinks, resolveBetLink, toBetLinkSet, type BetLinkSet } from "@/lib/bet-links";
import { slipScope } from "./book-links";

export type LegPromo = {
  readonly kind: PromoKind;
  readonly name: string;
  readonly cap: number | null;
  readonly minPicks: number;
  readonly maxPerLineup: number;
};

export type SlipLeg = {
  readonly rowId: string;
  readonly book: string;
  readonly league: string;
  readonly scope: string | null;
  readonly player: string;
  readonly team: string | null;
  readonly position: string | null;
  readonly play: PlayLine;
  readonly matchup: string;
  readonly hit: number;
  readonly odds: number | null;
  readonly line: number | null;
  readonly links: BetLinkSet | null;
  readonly promo: LegPromo | null;
  readonly multiplier: number | null;
  readonly addedAtMs: number;
};

export type SlipLegCurrent = {
  readonly hit: number;
  readonly odds: number | null;
  readonly line: number | null;
  readonly links: BetLinkSet | null;
  readonly multiplier: number | null;
  readonly pick: string | null;
};

export type SlipLegView = SlipLeg & {
  readonly current: SlipLegCurrent | null;
  readonly gone: boolean;
  readonly moved: boolean;
  readonly lineMoved: boolean;
};

export function legHref(leg: SlipLegView, isMobile: boolean): string | null {
  return resolveBetLink(leg.current?.links ?? leg.links, isMobile);
}

export type SlipTarget = {
  readonly id: string;
  readonly label: string;
  readonly legs: number;
  readonly platform: PlatformType;
  readonly base: SlipLadder;
  readonly ladder: SlipLadder;
  readonly payout: number;
  readonly basePayout: number;
  readonly boostPct: number;
  readonly allOrNothing: boolean;
  readonly leagueLadders: Readonly<Record<string, SlipLadder>> | null;
};

export type SlipPrice = {
  readonly multiplier: number;
  readonly factor: number;
  readonly promo: boolean;
};

export const LIST_PRICE: SlipPrice = { multiplier: 1, factor: 1, promo: false };

export type SlipEvaluation = {
  readonly filled: number;
  readonly remaining: number;
  readonly hitAllPct: number | null;
  readonly evPct: number | null;
  readonly roomPct: number | null;
  readonly hopeless: boolean;
  readonly needAllPct: number | null;
  readonly parlayOdds: number | null;
  readonly payout: number;
};

export const SLIP_STORAGE_VERSION = 6;
export const SLIP_STORAGE_KEY = `fantasy_props_slip_v${SLIP_STORAGE_VERSION}`;

export function legPromo(promo: DisplayPlayerProp["promo"]): LegPromo | null {
  return promo == null ? null : { kind: promo.kind, name: promo.name, cap: promo.cap, minPicks: promo.minPicks, maxPerLineup: promo.maxPerLineup };
}

export function legMultiple(leg: SlipLegView): number | null {
  return leg.current === null ? leg.multiplier : leg.current.multiplier;
}

export function slipPrice(legs: readonly SlipLegView[]): SlipPrice | null {
  let multiplier = 1;
  for (const leg of legs) {
    const multiple = legMultiple(leg);
    if (multiple === null) return null;
    multiplier *= multiple;
  }
  return { multiplier, factor: 1, promo: legs.some((leg) => leg.promo !== null) };
}

export function slipMinLegs(legs: readonly SlipLeg[]): number {
  return legs.reduce((acc, leg) => Math.max(acc, leg.promo?.minPicks ?? 0), 0);
}

export function slipCap(legs: readonly SlipLeg[]): number | null {
  return legs.find((leg) => leg.promo !== null)?.promo?.cap ?? null;
}

function scaleLadder(ladder: SlipLadder, multiplier: number): SlipLadder {
  if (multiplier === 1) return ladder;
  return Object.fromEntries(Object.entries(ladder).map(([correct, payout]) => [correct, payout * multiplier]));
}

const ONE_LEAGUE_BOOKS: ReadonlySet<string> = new Set(["dkpick6"]);

export function slipsStayInOneLeague(book: string): boolean {
  return ONE_LEAGUE_BOOKS.has(book);
}

export type SlipLock = { readonly league: string; readonly scope: string };

export function slipLock(legs: readonly SlipLeg[], book: string): SlipLock | null {
  if (!slipsStayInOneLeague(book) || legs.length === 0) return null;
  const [first] = legs;
  const scope = first.scope ?? first.league;
  return legs.every((leg) => (leg.scope ?? leg.league) === scope) ? { league: first.league, scope } : null;
}

export function promoPicks(legs: readonly SlipLeg[]): number {
  return legs.filter((leg) => leg.promo !== null).length;
}

export function promoLimit(legs: readonly SlipLeg[]): number | null {
  const limits = legs.flatMap((leg) => (leg.promo !== null && leg.promo.maxPerLineup > 0 ? [leg.promo.maxPerLineup] : []));
  return limits.length === 0 ? null : Math.min(...limits);
}

export function promoOverLimit(legs: readonly SlipLeg[]): number | null {
  const limit = promoLimit(legs);
  return limit !== null && promoPicks(legs) > limit ? limit : null;
}

export function rowIdOf(row: DisplayPlayerProp): string {
  return String(row.id).replace(/^props:/, "");
}

export function legFromRow(
  row: DisplayPlayerProp,
  book: string,
  lines: { readonly matchup: string; readonly time: string; readonly title: string; readonly play: PlayLine },
  nowMs = Date.now(),
): SlipLeg {
  const odds = typeof row.primary_odds === "number" ? row.primary_odds : null;
  return {
    rowId: rowIdOf(row),
    book,
    league: row.league ?? "",
    scope: slipScope(book, row.bet_link, row.league ?? ""),
    player: lines.title,
    team: row.team_short ?? (row.team || null),
    position: row.player_position || null,
    play: lines.play,
    matchup: lines.time ? `${lines.matchup} · ${lines.time}` : lines.matchup,
    hit: typeof row.odds_to_hit === "number" && Number.isFinite(row.odds_to_hit) ? row.odds_to_hit : 0,
    odds,
    line: typeof row.line === "number" ? row.line : null,
    links: row.__comparison_links?.[book] ?? toBetLinkSet(row.bet_link, null, null),
    promo: legPromo(row.promo),
    multiplier: row.payout_multiplier ?? null,
    addedAtMs: nowMs,
  };
}

export function withCurrent(leg: SlipLeg, row: DisplayPlayerProp): SlipLegView;
export function withCurrent(leg: SlipLeg, row: DisplayPlayerProp | undefined, boardLoaded: boolean): SlipLegView;
export function withCurrent(leg: SlipLeg, row: DisplayPlayerProp | undefined, boardLoaded = true): SlipLegView {
  if (row === undefined) return { ...leg, current: null, gone: boardLoaded, moved: false, lineMoved: false };
  const odds = typeof row.primary_odds === "number" ? row.primary_odds : null;
  const current: SlipLegCurrent = {
    hit: typeof row.odds_to_hit === "number" && Number.isFinite(row.odds_to_hit) ? row.odds_to_hit : leg.hit,
    odds,
    line: typeof row.line === "number" ? row.line : null,
    links: row.__comparison_links?.[leg.book] ?? toBetLinkSet(row.bet_link, null, null) ?? leg.links,
    multiplier: row.payout_multiplier ?? null,
    pick: row.book_pick_id ?? null,
  };
  const lineMoved = current.line !== leg.line;
  const moved = lineMoved || current.odds !== leg.odds || Math.abs(current.hit - leg.hit) >= 0.05;
  return { ...leg, current, gone: false, moved, lineMoved };
}

export function legProbability(leg: SlipLegView): number {
  return Math.min(0.999, Math.max(0.001, (leg.current?.hit ?? leg.hit) / 100));
}

export function sameLadder(a: SlipLadder, b: SlipLadder): boolean {
  const tiers = Object.keys(a);
  return tiers.length === Object.keys(b).length && tiers.every((tier) => a[Number(tier)] === b[Number(tier)]);
}

export function topPayout(ladder: SlipLadder): number {
  return ladder[Math.max(...Object.keys(ladder).map(Number))];
}

export function priceLadder(target: Pick<SlipTarget, "platform" | "boostPct" | "base">, price: SlipPrice, tiers: SlipLadder = target.base): SlipLadder {
  const scaled = scaleLadder(tiers, price.multiplier * price.factor);
  return (price.promo && promoBlocksBoost(target.platform)) || !(target.boostPct > 0) ? scaled : boostLadder(target.platform, scaled, target.boostPct);
}

function withBase(target: Omit<SlipTarget, "base" | "ladder" | "payout" | "basePayout" | "allOrNothing">, base: SlipLadder): SlipTarget {
  const ladder = priceLadder({ ...target, base }, LIST_PRICE);
  return { ...target, base, ladder, payout: topPayout(ladder), basePayout: topPayout(base), allOrNothing: Object.keys(base).length === 1 };
}

function targetFrom(platform: PlatformType, slip: SlipTypeBreakeven, boostPct: number): SlipTarget | null {
  if (!slip.ladder) return null;
  const legs = slipTypeLegCount(slip.id) || Math.max(...Object.keys(slip.ladder).map(Number));
  return withBase({ id: slip.id, label: slip.label, legs, platform, boostPct, leagueLadders: slip.leagues ?? null }, slip.ladder);
}

export function targetForLeague(target: SlipTarget, league: string | null): SlipTarget | null {
  if (league === null || target.leagueLadders === null) return target;
  const base = target.leagueLadders[league.toUpperCase()];
  return base === undefined ? null : withBase(target, base);
}

export function listSlipTargets(platform: PlatformType, boostPct: number, minLegs = 0, league: string | null = null): SlipTarget[] {
  return (getSlipBreakevens(platform) ?? [])
    .map((slip) => targetFrom(platform, slip, boostPct))
    .filter((target): target is SlipTarget => target !== null && target.legs >= minLegs)
    .flatMap((target) => targetForLeague(target, league) ?? []);
}

function bestTarget(targets: readonly SlipTarget[]): SlipTarget {
  return targets.reduce((best, target) => (breakevenPctFromLadder(target.ladder) < breakevenPctFromLadder(best.ladder) ? target : best), targets[0]);
}

export function resolveSlipTarget(platform: PlatformType, slipTypeId: string, boostPct: number, minLegs = 0, league: string | null = null, legs = 0): SlipTarget | null {
  const targets = listSlipTargets(platform, boostPct, minLegs, league);
  if (targets.length === 0) return null;
  const chosen = slipTypeId ? targets.find((target) => target.id === slipTypeId) : undefined;
  const exact = chosen === undefined && legs > 0 ? targets.filter((target) => target.legs === legs) : [];
  const picked = chosen ?? (exact.length > 0 ? bestTarget(exact) : bestTarget(targets));
  if (picked.legs >= legs) return picked;
  const bigger = targets.filter((target) => target.legs >= legs);
  if (bigger.length === 0) return null;
  const smallest = Math.min(...bigger.map((target) => target.legs));
  return bestTarget(bigger.filter((target) => target.legs === smallest));
}

export function correctCountDistribution(probs: readonly number[]): number[] {
  let dist = [1];
  for (const p of probs) {
    const next = new Array<number>(dist.length + 1).fill(0);
    dist.forEach((mass, k) => {
      next[k] += mass * (1 - p);
      next[k + 1] += mass * p;
    });
    dist = next;
  }
  return dist;
}

export function ladderEv(probs: readonly number[], ladder: SlipLadder): number {
  const dist = correctCountDistribution(probs);
  const expected = dist.reduce((sum, mass, correct) => sum + mass * (ladder[correct] ?? 0), 0);
  return expected - 1;
}

export function payoutToAmerican(payout: number): number | null {
  if (!Number.isFinite(payout) || payout <= 1) return null;
  return payout >= 2 ? Math.round((payout - 1) * 100) : Math.round(-100 / (payout - 1));
}

export function evaluateSlip(probs: readonly number[], target: SlipTarget, price: SlipPrice = LIST_PRICE, tiers?: SlipLadder): SlipEvaluation {
  const filled = Math.min(probs.length, target.legs);
  const used = probs.slice(0, filled);
  const remaining = target.legs - filled;
  const hitAll = used.length > 0 ? used.reduce((acc, p) => acc * p, 1) : null;
  const ladder = priceLadder(target, price, tiers);
  const payout = topPayout(ladder);
  const needAllPct = Object.keys(ladder).length === 1 ? 100 / payout : null;
  const parlayOdds = payoutToAmerican(payout);
  if (remaining === 0) {
    const ev = ladderEv(used, ladder);
    return { filled, remaining, hitAllPct: hitAll === null ? null : hitAll * 100, evPct: ev * 100, roomPct: null, hopeless: false, needAllPct, parlayOdds, payout };
  }
  const evAt = (p: number) => ladderEv([...used, ...new Array<number>(remaining).fill(p)], ladder);
  if (evAt(0.999) < 0) {
    return { filled, remaining, hitAllPct: hitAll === null ? null : hitAll * 100, evPct: null, roomPct: null, hopeless: true, needAllPct, parlayOdds, payout };
  }
  let low = 0;
  let high = 0.999;
  for (let step = 0; step < 48; step++) {
    const mid = (low + high) / 2;
    if (evAt(mid) < 0) low = mid;
    else high = mid;
  }
  return { filled, remaining, hitAllPct: hitAll === null ? null : hitAll * 100, evPct: null, roomPct: Math.round(high * 1000) / 10, hopeless: false, needAllPct, parlayOdds, payout };
}

export function slipLegsText(legs: readonly SlipLegView[], book: string, target: SlipTarget | null, payout: number): string {
  const header = target ? `${book} · ${target.label} · ${payout.toFixed(2).replace(/\.?0+$/, "")}x` : book;
  const lines = legs.map((leg, index) => {
    const hit = leg.current?.hit ?? leg.hit;
    const odds = leg.current?.odds ?? leg.odds;
    const promo = leg.promo === null ? "" : ` · ${leg.promo.name}`;
    return `${index + 1}. ${leg.player} ${playText(leg.play)}${promo} · ${hit.toFixed(1)}%${odds === null ? "" : ` (${odds > 0 ? `+${odds}` : odds})`}`;
  });
  return [header, ...lines].join("\n");
}

export type StoredSlip = { readonly legs: SlipLeg[]; readonly target: string | null };

export const EMPTY_SLIP: StoredSlip = { legs: [], target: null };

export function coerceStoredSlip(raw: unknown, book: string): StoredSlip {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return EMPTY_SLIP;
  const value = raw as { v?: unknown; legs?: unknown; target?: unknown };
  if (value.v !== SLIP_STORAGE_VERSION || !Array.isArray(value.legs)) return EMPTY_SLIP;
  const target = typeof value.target === "string" && value.target !== "" ? value.target : null;
  const seen = new Set<string>();
  const legs: SlipLeg[] = [];
  for (const entry of value.legs) {
    if (!entry || typeof entry !== "object") continue;
    const leg = entry as Record<string, unknown>;
    if (typeof leg.rowId !== "string" || leg.rowId === "" || seen.has(leg.rowId) || leg.book !== book) continue;
    if (typeof leg.player !== "string" || typeof leg.hit !== "number" || typeof leg.league !== "string" || !isStoredBetLinks(leg.links)) continue;
    const play = storedPlay(leg.play);
    if (play === null) continue;
    seen.add(leg.rowId);
    legs.push({
      rowId: leg.rowId,
      book,
      league: leg.league,
      scope: typeof leg.scope === "string" ? leg.scope : null,
      player: leg.player,
      team: typeof leg.team === "string" && leg.team !== "" ? leg.team : null,
      position: typeof leg.position === "string" && leg.position !== "" ? leg.position : null,
      play,
      matchup: typeof leg.matchup === "string" ? leg.matchup : "",
      hit: leg.hit,
      odds: typeof leg.odds === "number" ? leg.odds : null,
      line: typeof leg.line === "number" ? leg.line : null,
      links: leg.links,
      promo: storedPromo(leg.promo),
      multiplier: typeof leg.multiplier === "number" && leg.multiplier > 0 ? leg.multiplier : null,
      addedAtMs: typeof leg.addedAtMs === "number" ? leg.addedAtMs : 0,
    });
  }
  return { legs, target };
}

function storedPlay(raw: unknown): PlayLine | null {
  if (!raw || typeof raw !== "object") return null;
  const play = raw as Record<string, unknown>;
  if ((play.side !== "Over" && play.side !== "Under") || typeof play.line !== "string" || typeof play.market !== "string") return null;
  return { side: play.side, line: play.line, was: typeof play.was === "string" ? play.was : null, market: play.market };
}

function storedPromo(raw: unknown): LegPromo | null {
  if (!raw || typeof raw !== "object") return null;
  const promo = raw as Record<string, unknown>;
  const kind = PROMO_KINDS.find((known) => known === promo.kind);
  if (kind === undefined || typeof promo.name !== "string") return null;
  if (typeof promo.minPicks !== "number" || typeof promo.maxPerLineup !== "number") return null;
  return { kind, name: promo.name, cap: typeof promo.cap === "number" ? promo.cap : null, minPicks: promo.minPicks, maxPerLineup: promo.maxPerLineup };
}
